import { z } from 'zod';
import { esquemaId, esquemaMotivo } from './comunes.js';
/**
 * Mensajeria asincrona segura (F4.8, MSG-01, plan maestro §9).
 *
 * - Una conversacion es de UNA paciente y tiene tema; es `clinica` (sintomas,
 *   dieta, tratamiento) o `administrativa` (horarios, pagos). Recepcion atiende
 *   lo administrativo y NO lee lo clinico (§9.2): son permisos distintos.
 * - Puede referirse a un plan y su revision, o a una comida: el mensaje lo
 *   cita, no lo cambia. Un cambio de tratamiento se hace en el plan (AC-08).
 * - Los mensajes no se editan ni se borran; cerrar y reabrir dejan rastro.
 * - El canal NO es de urgencias, y el plazo de respuesta se dice siempre.
 */
export const TIPOS_DE_CONVERSACION = ['clinica', 'administrativa'];
export const ESTADOS_DE_CONVERSACION = [
    'nuevo',
    'asignado',
    'en-atencion',
    'pendiente-paciente',
    'cerrado',
];
export const NOMBRE_DE_ESTADO_DE_CONVERSACION = {
    nuevo: 'Nuevo',
    asignado: 'Asignado',
    'en-atencion': 'En atención',
    'pendiente-paciente': 'Esperando a la paciente',
    cerrado: 'Cerrado',
};
/**
 * Plazo de respuesta que se le muestra a la paciente, en horas habiles. Es el
 * compromiso operativo por defecto; lo ajusta el dueño (pendiente de politica).
 */
export const PLAZO_DE_RESPUESTA_HORAS = 48;
/** Texto fijo que acompaña al canal. Sin marca y sin numeros que dependan del pais. */
export const AVISO_DE_NO_URGENCIAS = 'Este canal no es para urgencias ni significa que alguien esté vigilando su salud en todo momento. Si tiene una urgencia, acuda a un servicio de urgencias.';
/** Antispam: conversaciones abiertas por paciente a la vez. */
export const MAXIMO_DE_CONVERSACIONES_ABIERTAS = 10;
export const esquemaReferenciaDeConversacion = z
    .object({
    tipo: z.literal('plan'),
    planId: esquemaId,
    /** La revision a la que se refiere la duda; se valida que exista. */
    revision: z.number().int().min(1),
    /** Una comida o actividad concreta, en texto (p. ej. «Desayuno de entre semana»). */
    detalle: z.string().trim().max(120).default(''),
})
    .strict();
const texto = z.string().trim().min(1, 'Escriba su mensaje').max(4000);
const adjuntos = z.array(esquemaId).max(5).default([]);
export const esquemaAbrirConversacion = z
    .object({
    tipo: z.enum(TIPOS_DE_CONVERSACION),
    tema: z.string().trim().min(3, 'Escriba el tema').max(120),
    referencia: esquemaReferenciaDeConversacion.optional(),
    texto,
    adjuntoIds: adjuntos,
})
    .strict()
    .refine((c) => !c.referencia || c.tipo === 'clinica', {
    message: 'Una duda sobre un plan es clínica',
    path: ['tipo'],
});
export const esquemaEnviarMensaje = z.object({ texto, adjuntoIds: adjuntos }).strict();
export const esquemaAsignarConversacion = z
    .object({ responsableId: esquemaId, suplenteId: esquemaId.optional() })
    .strict()
    .refine((a) => a.suplenteId !== a.responsableId, {
    message: 'El suplente tiene que ser otra persona',
    path: ['suplenteId'],
});
export const esquemaCambiarEstadoDeConversacion = z
    .object({
    estado: z.enum(['en-atencion', 'pendiente-paciente', 'cerrado', 'nuevo']),
    motivo: esquemaMotivo.optional(),
})
    .strict()
    .refine((c) => c.estado !== 'cerrado' || c.motivo !== undefined, {
    message: 'Cerrar exige motivo',
    path: ['motivo'],
});
//# sourceMappingURL=mensajeria.js.map